'use client';

import { memo, useEffect, useRef, useState } from 'react';
import Image from 'next/image';
import clsx from 'clsx';

import { completeVideoApi, useModuleStore, Video } from '@entities/module';
import { Modal } from '@shared/ui/components';
import PlayIcon from '@public/icons/play.svg';
import CheckIcon from '@public/icons/check-thin.svg';

import styles from './VideoCard.module.scss';

interface VideoCardProps {
  moduleId: string;
  video: Video;
  inline?: boolean;
}

const VideoCard = memo((props: VideoCardProps) => {
  const { moduleId, video, inline } = props;

  const activeVideoId = useModuleStore((state) => state.activeVideoId);
  const setActiveVideoId = useModuleStore((state) => state.setActiveVideoId);
  const completeVideo = useModuleStore((state) => state.completeVideo);

  const completeTimeout = useRef<NodeJS.Timeout>();
  const [iframeVisible, setIframeVisible] = useState<boolean>(false);

  useEffect(() => {
    if (inline) {
      setIframeVisible(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (
      iframeVisible &&
      !video.completed &&
      ((activeVideoId && activeVideoId === video.id) || inline)
    ) {
      completeTimeout.current = setTimeout(() => {
        completeVideoApi({
          module_id: moduleId,
          video_id: video.id,
        }).then(() => {
          completeVideo(moduleId, video.id);
        });
      }, 1000);
    }

    return () => {
      clearTimeout(completeTimeout.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [iframeVisible]);

  if (inline) {
    return (
      <div data-video={video.title} className={styles.inlineContainer}>
        <div className={styles.inlineContainer_header}>{video.title}</div>

        {iframeVisible && (
          <iframe
            src={video.url}
            title={video.title}
            className={styles.inlineContainer_video}
            loading="lazy"
            allow="accelerometer; gyroscope; autoplay; encrypted-media;"
            allowFullScreen
          />
        )}
      </div>
    );
  }

  return (
    <>
      <Modal
        isOpen={activeVideoId === video.id}
        header={video.title}
        className={styles.modal}
        headerClassName={styles.modal_header}
        bodyClassName={styles.modal_body}
        close={() => setActiveVideoId(undefined)}
        onEntered={() => setIframeVisible(true)}
        onClose={() => setIframeVisible(false)}
      >
        {iframeVisible && (
          <iframe
            src={video.url}
            title={video.title}
            className={styles.video}
            loading="lazy"
            allow="accelerometer; gyroscope; autoplay; encrypted-media;"
            allowFullScreen
          />
        )}
      </Modal>

      <div className={styles.container}>
        <div className={styles.title}>
          <Image
            src={CheckIcon}
            className={clsx(styles.check, video.completed && styles.check_done)}
            alt="Check"
            draggable={false}
          />

          <div>{video.title}</div>
        </div>

        <div className={styles.preview} onClick={() => setActiveVideoId(video.id)}>
          <Image
            src={video.preview}
            className={styles.preview_image}
            alt={`${video.title} preview`}
            width={100}
            height={100}
            draggable={false}
          />

          <Image src={PlayIcon} className={styles.preview_play} alt="Play" draggable={false} />
        </div>

        {video.description && <div className={styles.description}>{video.description}</div>}
      </div>
    </>
  );
});

export default VideoCard;
